import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { clientApi } from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import { format } from 'date-fns';
import { CLIENT_STATUS_OPTIONS as STATUSES } from '../../utils/statusLabels';
import { selectCompanyName } from '../../store/slices/authSlice';

// Statuses that have detail available to view
const VIEWABLE_STATUSES = [
  'ASSIGNED', 'VENDOR_SUBMITTED', 'COMPANY_APPROVED', 'COMPANY_REJECTED',
  'PENDING_FINAL_APPROVAL', 'CLIENT_APPROVED', 'CLIENT_REJECTED', 'CVA_REJECTED', 'CANCELLED',
];

// ── Reusable sortable header cell ────────────────────────────────────
function SortTh({ col, label, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-800 hover:bg-gray-100 transition-colors"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? <span className="text-blue-500">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
          : <span className="text-gray-300"> ⇅</span>}
      </span>
    </th>
  );
}

export default function ClientRequests() {
  const navigate     = useNavigate();
  const location     = useLocation();
  const companyName  = useSelector(selectCompanyName);
  const [page, setPage]     = useState(0);
  // Pre-populate status from navigation state (e.g. "Review now →" on dashboard)
  const [status, setStatus] = useState(location.state?.status || '');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  const [sortCol, setSortCol] = useState('updatedAt');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(0);
  };

  const { data, isLoading } = useQuery(
    ['client-requests', page, status, debouncedSearch, sortCol, sortDir],
    () => clientApi.getFinishedGoods({ page, size: 15, status: status && status !== 'REJECTED' ? status : undefined, rejected: status === 'REJECTED' ? true : undefined, search: debouncedSearch || undefined, sort: `${sortCol},${sortDir}` }),
    { keepPreviousData: true }
  );

  const content    = data?.data?.content || [];
  const totalPages = data?.data?.totalPages || 0;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {companyName ? `${companyName} Requests` : 'My Requests'}
        </h1>
        <p className="text-sm text-gray-500 mt-0.5">All finished good requests from Salesforce</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by FG code, vendor name, factory..."
          className="w-72 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
        {isLoading ? <LoadingSpinner /> : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
              <SortTh col="bobFinishedGoodCode" label="FG Code"      sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="category"             label="Category"     sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="factoryCode"          label="Factory Code" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="factoryName"          label="Factory Name" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="quantity"             label="Quantity"     sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="status"               label="Status"       sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <SortTh col="updatedAt"            label="Last Updated" sortCol={sortCol} sortDir={sortDir} onSort={toggleSort} />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {content.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">No requests found</td></tr>
                ) : content.map((fg) => {
                  const isViewable = VIEWABLE_STATUSES.includes(fg.status);
                  const needsReview = fg.status === 'COMPANY_APPROVED';
                  const isAssigned  = fg.status === 'ASSIGNED';
                  return (
                    <tr key={fg.id} className={`hover:bg-gray-50 ${isViewable ? 'cursor-pointer' : ''}`}
                      onClick={() => isViewable && navigate(`/client/review/${fg.id}`)}>
                      <td className="px-4 py-3 font-mono text-xs text-blue-700 whitespace-nowrap" title={fg.fgName}>{fg.bobFinishedGoodCode || '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{fg.category || '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{fg.factoryCode || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 max-w-40 truncate" title={fg.factoryName}>{fg.factoryName || '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{fg.quantity ?? '—'} {fg.unitOfMeasure}</td>
                      <td className="px-4 py-3"><StatusBadge status={fg.status} /></td>
                      <td className="px-4 py-3 text-xs text-gray-400">
                        {fg.updatedAt ? format(new Date(fg.updatedAt), 'dd MMM yyyy') : '—'}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {isViewable ? (
                          <button
                            onClick={() => navigate(`/client/review/${fg.id}`)}
                            className={`text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${
                              needsReview
                                ? 'text-green-700 border-green-200 hover:bg-green-50'
                                : isAssigned
                                  ? 'text-purple-600 border-purple-200 hover:bg-purple-50'
                                  : 'text-blue-600 border-blue-200 hover:bg-blue-50'
                            }`}
                          >
                            {needsReview ? 'Review →' : 'View →'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
